/**
 * Presentation Viewer Component
 * Renders PDF slide decks with navigation controls using PDF.js
 */

import { useState, useEffect, useRef, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { getExerciseContentUrl } from "@services/exercises";

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface PresentationViewerProps {
  exerciseId: string;
  title?: string;
  className?: string;
}

// Inline SVG icons
const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const ExpandIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
    />
  </svg>
);

const ExternalLinkIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

const SpinnerIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const FileIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export function PresentationViewer({
  exerciseId,
  title,
  className = "",
}: PresentationViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [pdfDocument, setPdfDocument] =
    useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fetch the signed URL
  useEffect(() => {
    async function fetchPdfUrl() {
      try {
        setLoading(true);
        setError(null);
        const response = await getExerciseContentUrl(exerciseId);
        setPdfUrl(response.content_url);
      } catch (err) {
        console.error("Failed to get PDF URL:", err);
        setError("Failed to load presentation. Please try again.");
        setLoading(false);
      }
    }

    fetchPdfUrl();
  }, [exerciseId]);

  // Load the PDF document
  useEffect(() => {
    if (!pdfUrl) return;

    // Capture pdfUrl value for TypeScript narrowing inside async function
    const url = pdfUrl;

    async function loadPdf() {
      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load PDF:", err);
        setError("Failed to load presentation. The file may be corrupted.");
        setLoading(false);
      }
    }

    loadPdf();
  }, [pdfUrl]);

  // Render the current page
  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) return;

    async function renderPage() {
      const page = await pdfDocument!.getPage(currentPage);
      const canvas = canvasRef.current!;
      const context = canvas.getContext("2d")!;

      // Calculate scale to fit the container
      const container = containerRef.current;
      if (container) {
        const containerWidth = container.clientWidth - 32; // Account for padding
        const viewport = page.getViewport({ scale: 1 });
        const calculatedScale = containerWidth / viewport.width;

        const scaledViewport = page.getViewport({ scale: calculatedScale });
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
          canvas: canvas,
        }).promise;
      }
    }

    renderPage();
  }, [pdfDocument, currentPage]);

  // Navigation handlers
  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  }, [currentPage]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  }, [currentPage, totalPages]);

  // Fullscreen handlers - defined before useEffect that uses them
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ignore if typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          goToPrevPage();
          break;
        case "ArrowRight":
        case "ArrowDown":
        case " ": // Space
          e.preventDefault();
          goToNextPage();
          break;
        case "f":
        case "F":
          e.preventDefault();
          toggleFullscreen();
          break;
        case "Escape":
          if (isFullscreen) {
            exitFullscreen();
          }
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    goToPrevPage,
    goToNextPage,
    isFullscreen,
    toggleFullscreen,
    exitFullscreen,
  ]);

  // Listen for fullscreen changes
  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center ${className}`}
      >
        <SpinnerIcon className="w-8 h-8 mx-auto text-primary-500 animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          Loading presentation...
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center ${className}`}
      >
        <FileIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Unable to Load Presentation
        </h3>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${
        isFullscreen ? "fixed inset-0 z-50 rounded-none border-none" : ""
      } ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-2">
          {title && (
            <h3 className="font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            title="Toggle fullscreen (F)"
          >
            <ExpandIcon className="w-4 h-4" />
            {isFullscreen ? "Exit" : "Fullscreen"}
          </button>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <ExternalLinkIcon className="w-4 h-4" />
              Open
            </a>
          )}
        </div>
      </div>

      {/* Canvas container */}
      <div
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4 ${
          isFullscreen ? "h-[calc(100vh-120px)]" : "min-h-[500px]"
        }`}
      >
        <canvas
          ref={canvasRef}
          className="shadow-lg max-w-full"
          style={{ maxHeight: isFullscreen ? "calc(100vh - 150px)" : "600px" }}
        />
      </div>

      {/* Navigation controls */}
      <div className="flex items-center justify-center gap-4 p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <button
          onClick={goToPrevPage}
          disabled={currentPage <= 1}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Previous slide (Left Arrow)"
        >
          <ChevronLeftIcon className="w-5 h-5" />
          Prev
        </button>

        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[100px] text-center">
          Slide {currentPage} of {totalPages}
        </span>

        <button
          onClick={goToNextPage}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next slide (Right Arrow)"
        >
          Next
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Keyboard shortcuts hint (only in fullscreen) */}
      {isFullscreen && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 dark:text-gray-400 bg-black/50 px-3 py-1 rounded-full">
          Use arrow keys to navigate | ESC to exit
        </div>
      )}
    </div>
  );
}
